import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MarkdownContent } from '@/components/markdown-content'
import { getMyPendingAnnouncements, markAnnouncementRead } from '@/lib/announcements-api'
import { getCurrentUserRole } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'

// Shows published in-app announcements one after another. The dialog cannot be
// dismissed; only confirming with "OK" stores the read receipt.
export function AnnouncementDialog() {
  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const profile = profileQuery.data

  if (!profile?.is_active) return null
  // Keyed per user so confirmations never leak across an account switch.
  return <PendingAnnouncementsDialog key={profile.id} userId={profile.id} />
}

function PendingAnnouncementsDialog({ userId }: { userId: string }) {
  const queryClient = useQueryClient()
  // Hides confirmed announcements immediately instead of waiting for the refetch.
  const [confirmedIds, setConfirmedIds] = useState<string[]>([])

  const pendingQuery = useQuery({
    queryKey: [...queryKeys.myPendingAnnouncements, userId],
    queryFn: getMyPendingAnnouncements,
  })

  const confirmMutation = useMutation({
    mutationFn: markAnnouncementRead,
    onSuccess: (_, id) => {
      setConfirmedIds((ids) => [...ids, id])
      queryClient.invalidateQueries({ queryKey: queryKeys.myPendingAnnouncements })
    },
    onError: (error, id) => {
      if (error.message !== 'ANNOUNCEMENT_NOT_AVAILABLE') {
        toast.error(getErrorMessage(error))
        return
      }
      // Withdrawn or expired in the meantime: just move on.
      setConfirmedIds((ids) => [...ids, id])
      queryClient.invalidateQueries({ queryKey: queryKeys.myPendingAnnouncements })
    },
  })

  const pending = (pendingQuery.data ?? []).filter(
    (announcement) => !confirmedIds.includes(announcement.id),
  )
  const current = pending[0]

  return (
    <Dialog open={Boolean(current)}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="max-w-lg"
      >
        {current && (
          <>
            <DialogHeader>
              <DialogTitle>{current.title}</DialogTitle>
              {pending.length > 1 && (
                <DialogDescription>{`Benachrichtigung 1 von ${pending.length}`}</DialogDescription>
              )}
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto">
              <MarkdownContent>{current.body}</MarkdownContent>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => confirmMutation.mutate(current.id)}
                disabled={confirmMutation.isPending}
                autoFocus
              >
                OK
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
